import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
	PaginatedResponse,
	PaginationMeta,
	PaginationParams,
	UsePaginationOptions,
	UsePaginationReturn,
} from '../types/pagination';

/**
 * Reusable pagination hook for any paginated API endpoint
 */
export function usePagination<TData, TParams extends PaginationParams>(
	fetchFunction: (params: TParams) => Promise<PaginatedResponse<TData>>,
	initialParams: Partial<TParams> = {},
	options: UsePaginationOptions = {}
): UsePaginationReturn<TData, TParams> {
	const { initialPage = 1, initialLimit = 20, autoFetch = true } = options;

	const [data, setData] = useState<TData[]>([]);
	const [pagination, setPagination] = useState<PaginationMeta | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Memoize initial params to prevent re-initialization
	const memoizedInitialParams = useMemo(
		() => ({
			page: initialPage,
			limit: initialLimit,
			...initialParams,
		}),
		[initialPage, initialLimit, JSON.stringify(initialParams)]
	);

	const [params, setParams] = useState<TParams>(memoizedInitialParams as TParams);

	// Force update params when memoizedInitialParams change (e.g., when navigating with different filters)
	useEffect(() => {
		setParams(memoizedInitialParams as TParams);
		setData([]); // Clear stale data
		setPagination(null); // Clear stale pagination
	}, [JSON.stringify(memoizedInitialParams)]);

	// Use ref to store current params to avoid stale closures
	const paramsRef = useRef(params);
	paramsRef.current = params;

	const fetchData = useCallback(
		async (customParams?: Partial<TParams>) => {
			setLoading(true);
			setError(null);

			try {
				const currentParams = paramsRef.current;
				const finalParams = customParams
					? { ...currentParams, ...customParams }
					: currentParams;

				const response = await fetchFunction(finalParams as TParams);
				setData(response.data);
				setPagination(response.pagination);
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
				setError(errorMessage);
				console.error('Pagination fetch error:', err);
			} finally {
				setLoading(false);
			}
		},
		[] // Remove fetchFunction dependency to prevent cascade re-renders
	);

	// Use a ref to track if we've already made the initial call
	const hasInitializedRef = useRef(false);

	// Store fetchFunction in ref to avoid stale closures
	const fetchFunctionRef = useRef(fetchFunction);
	fetchFunctionRef.current = fetchFunction;

	// Track previous params to prevent unnecessary fetches
	const prevParamsRef = useRef<string>('');
	const isFetchingRef = useRef<boolean>(false);

	// Single effect for both initialization and parameter changes
	useEffect(() => {
		if (!autoFetch) return;

		const currentParamsString = JSON.stringify(paramsRef.current);

		// Skip if params haven't actually changed
		if (hasInitializedRef.current && prevParamsRef.current === currentParamsString) {
			return;
		}

		// Skip if already fetching (prevents React.StrictMode double calls)
		if (isFetchingRef.current) {
			return;
		}

		const performFetch = async () => {
			// Set fetching flag to prevent concurrent calls
			isFetchingRef.current = true;
			setLoading(true);
			setError(null);

			try {
				const response = await fetchFunctionRef.current(paramsRef.current);
				setData(response.data);
				setPagination(response.pagination);
				prevParamsRef.current = currentParamsString;
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
				setError(errorMessage);
				console.error('Pagination fetch error:', err);
			} finally {
				setLoading(false);
				isFetchingRef.current = false;
			}
		};

		if (!hasInitializedRef.current) {
			// Initial fetch
			hasInitializedRef.current = true;
			performFetch();
		} else {
			// Parameter change fetch (only after initialization and if params changed)
			performFetch();
		}
	}, [autoFetch, params]); // Remove fetchData dependency

	// Navigation functions
	const goToPage = useCallback((page: number) => {
		setParams(prev => ({ ...prev, page }));
	}, []);

	const goToNextPage = useCallback(() => {
		if (pagination?.hasNext) {
			goToPage(pagination.page + 1);
		}
	}, [pagination, goToPage]);

	const goToPreviousPage = useCallback(() => {
		if (pagination?.hasPrevious) {
			goToPage(pagination.page - 1);
		}
	}, [pagination, goToPage]);

	const goToFirstPage = useCallback(() => {
		goToPage(1);
	}, [goToPage]);

	const goToLastPage = useCallback(() => {
		if (pagination?.pages) {
			goToPage(pagination.pages);
		}
	}, [pagination, goToPage]);

	// Filter and search functions
	const updateParams = useCallback((newParams: Partial<TParams>) => {
		setParams(prev => ({ ...prev, ...newParams, page: 1 })); // Reset to page 1 on filter change
	}, []);

	const search = useCallback(
		(searchTerm: string) => {
			updateParams({ search: searchTerm } as Partial<TParams>);
		},
		[updateParams]
	);

	const changeLimit = useCallback(
		(limit: number) => {
			updateParams({ limit, page: 1 } as Partial<TParams>);
		},
		[updateParams]
	);

	const clearFilters = useCallback(() => {
		setParams({
			page: 1,
			limit: initialLimit,
		} as TParams);
	}, [initialLimit]);

	return {
		// Data
		data,
		pagination,
		loading,
		error,

		// Navigation
		goToPage,
		goToNextPage,
		goToPreviousPage,
		goToFirstPage,
		goToLastPage,

		// Filtering
		updateParams,
		search,
		changeLimit,
		clearFilters,

		// Manual control
		refresh: fetchData,
		currentParams: params,
	};
}
